<template>
  <v-container fill-height>
    <!-- Loader -->
    <div v-if="isLoading" class="d-flex align-center justify-center flex-column fill-height">
        <v-progress-circular indeterminate :color="colors.primary" size="64"></v-progress-circular>
    </div>

    <!-- Redirect if no jury -->
    <div v-else-if="!jury">
      <p>Vous devez d'abord sélectionner un jury.</p>
      <v-btn @click="redirectToSelectJury">Sélectionner Jury</v-btn>
    </div>

    <!-- Candidates and Criteria Selection -->
    <div v-else>
      <v-app-bar :elevation="2" :style="{ backgroundColor: colors.primary }">
        <v-app-bar-title :style="{ color: colors.secondary }">BAOBAB CHALLENGE 2024</v-app-bar-title>

        <v-btn icon id="menu-activator">
          <v-avatar size="36px">
            <v-img alt="Jury" :src="getJuryImageUrl(jury?.image)"></v-img>
          </v-avatar>
        </v-btn>
      <v-menu activator="#menu-activator">
        <v-list>
          <v-list-item link>
            <v-list-item-title @click="showResults">Resultats</v-list-item-title>
          </v-list-item>
          <v-list-item link>
            <v-list-item-title @click="logoutUser">Déconnexion</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      </v-app-bar>
      
      <!-- Dropdown for Selecting Candidate -->
      <div :style="{ borderBottom: `1px solid ${colors.grey}` }" justify="center" align="center">
        <v-select
          :items="candidatesList"
          item-title="name"
          item-value="id"
          v-model="currentCandidate"
          label="Sélectionnez un candidat"
          outlined
          dense
          class="mt-4"
          width="280"
          @update:modelValue="onCandidateSelected"
        ></v-select>
      </div>

      <!-- Message if no candidate selected -->
      <p v-if="!currentCandidate" class="text-center mt-4">
        Veuillez d'abord sélectionner un candidat.
      </p>

      <!-- Steps and Criterias -->
      <div v-else class="steps-container mt-4">
        <v-card class="pa-4" :style="{ width: '200px', margin: 'auto' }" elevation="0">
            <div class="text-center">
              <v-img style="border:1px solid #e5e5e5;" alt="Candidate" :src="getCandidateImageUrl(selectedCandidate?.image)" max-width="200"></v-img>
              <h3 style="max-width: 200px">{{selectedCandidate?.name}}</h3>
            </div>
        </v-card>
        <div v-if="!loading">
          <v-stepper :items="criterias.map((criteria, index) => `${criteria.category}`)">
            <!-- Step 1 -->
            <template v-slot:item.1>
              <v-card :title="criterias[0].category" flat>
                <v-container>
                  <v-row v-for="criterium in JSON.parse(criterias[0].criterias)" :key="criterium.id">
                    <!-- Left column: Criteria and Explanation -->
                    <v-col md="6" sm="12" cols="12" align-self="start">
                      <p class="text-left"><strong>{{ criterium.criteria }}</strong></p>
                      <p class="text-left" v-html="formatExplanation(criterium.explanation)"></p>
                    </v-col>
                    <!-- Right column: Rating or Slider -->
                    <v-col md="6" sm="12" cols="12">
                      <template v-if="criterium.max_score <= 10">
                        <!-- Star Rating (5 stars) -->
                        <v-rating
                          v-model="ratings[criterium.id]"
                          :length="criterium.max_score"
                          size="large"
                          hover
                          dense
                          :active-color="colors.yellow"
                          clearable
                        ></v-rating>
                      </template>
                      <template v-else>
                        <!-- Slider for scores > 10 -->
                        <v-slider
                          v-model="ratings[criterium.id]"
                          :max="criterium.max_score"
                          step="1"
                          thumb-label
                          dense
                          :color="colors.yellow"
                          show-ticks
                        ></v-slider>
                      </template>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </template>

            <template v-slot:item.2>
              <v-card :title="criterias[1].category" flat>
                <v-container>
                  <v-row v-for="criterium in JSON.parse(criterias[1].criterias)" :key="criterium.id">
                    <!-- Left column: Criteria and Explanation -->
                    <v-col md="6" sm="12" cols="12" align-self="start">
                      <p class="text-left"><strong>{{ criterium.criteria }}</strong></p>
                      <p class="text-left" v-html="formatExplanation(criterium.explanation)"></p>
                    </v-col>
                    <!-- Right column: Rating or Slider -->
                    <v-col md="6" sm="12" cols="12">
                      <template v-if="criterium.max_score <= 10">
                        <!-- Star Rating (5 stars) -->
                        <v-rating
                          v-model="ratings[criterium.id]"
                          :length="criterium.max_score"
                          size="large"
                          hover
                          dense
                          :active-color="colors.yellow"
                          clearable
                        ></v-rating>
                      </template>
                      <template v-else>
                        <!-- Slider for scores > 10 -->
                        <v-slider
                          v-model="ratings[criterium.id]"
                          :max="criterium.max_score"
                          step="1"
                          thumb-label
                          dense
                          :color="colors.yellow"
                          show-ticks
                        ></v-slider>
                      </template>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </template>

            <template v-slot:item.3>
              <v-card :title="criterias[2].category" flat>
                <v-container>
                  <v-row v-for="criterium in JSON.parse(criterias[2].criterias)" :key="criterium.id">
                    <!-- Left column: Criteria and Explanation -->
                    <v-col md="6" sm="12" cols="12" align-self="start">
                      <p class="text-left"><strong>{{ criterium.criteria }}</strong></p>
                      <p class="text-left" v-html="formatExplanation(criterium.explanation)"></p>
                    </v-col>
                    <!-- Right column: Rating or Slider -->
                    <v-col md="6" sm="12" cols="12">
                      <template v-if="criterium.max_score <= 10">
                        <!-- Star Rating (5 stars) -->
                        <v-rating
                          v-model="ratings[criterium.id]"
                          :length="criterium.max_score"
                          size="large"
                          hover
                          dense
                          :active-color="colors.yellow"
                          clearable
                        ></v-rating>
                      </template>
                      <template v-else>
                        <!-- Slider for scores > 10 -->
                        <v-slider
                          v-model="ratings[criterium.id]"
                          :max="criterium.max_score"
                          step="1"
                          thumb-label
                          dense
                          :color="colors.yellow"
                          show-ticks
                        ></v-slider>
                      </template>
                    </v-col>
                  </v-row>
                  <v-row>
                    <!-- Submit Button on Last Step -->
                    <v-col cols="12" align-self="center">
                      <v-btn @click="submitResults" :color="colors.primary">Submit</v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </template>
          </v-stepper>
        </div>
        <v-row v-if="loading" justify="center" align="center" class="fill-height">
          <v-progress-circular indeterminate :color="colors.primary" size="64"></v-progress-circular>
        </v-row>
      </div>
    </div>

    <v-footer border app class="d-flex justify-center align-center p-4">
      Développé par : <a href="https://thewebsquare.com" target="_blank" :style="{ color: colors.primary }">The Web Square</a>
    </v-footer>

    <!-- <v-dialog
      v-model="submitDialog"
      max-width="320"
      persistent
    >
      <v-list
        class="py-2"
        :color="colors.primary"
        elevation="12"
        rounded="lg"
      >
        <v-list-item
          title="Soumission en cours..."
        >
          <template v-slot:append>
            <v-progress-circular
              :color="colors.primary"
              indeterminate="disable-shrink"
              size="16"
              width="2"
            ></v-progress-circular>
          </template>
        </v-list-item>
      </v-list>
    </v-dialog> -->
    <v-dialog
      v-model="submitDialog"
      max-width="320"
      persistent
    >
      <v-list
        class="py-2"
        :color="colors.primary"
        elevation="12"
        rounded="lg"
      >
        <v-list-item
          title="Soumission en cours..."
        >
          <template v-slot:append>
            <v-progress-circular
              :color="colors.primary"
              indeterminate="disable-shrink"
              size="16"
              width="2"
            ></v-progress-circular>
          </template>
        </v-list-item>
      </v-list>
    </v-dialog>

    <!-- After submission -->
    <v-dialog v-model="submissionSuccessDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h5" justify="center" align="center">Evaluation soumise</v-card-title>
        <v-card-text justify="center" align="center">
          <div><v-icon icon="mdi-check" color="green" size="100"></v-icon></div>
          <p>Votre évaluation a été soumise avec succès !</p>
        </v-card-text>
        <v-card-text style="flex-direction: column;" justify="center" align="center">
          <v-btn class="mb-3" @click="evaluateAnotherCandidate" :color="colors.primary" >Evaluer un autre candidat</v-btn>
          <v-btn @click="showResults" color="primary">Voir les résultats</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Error dialog -->
    <v-dialog v-model="showErrorDialog" max-width="320" persistent>
      <v-list class="py-2" color="red" elevation="12" rounded="lg">
        <v-list-item>
          <v-list-item-title justify="center" align="center">
            <div><v-icon icon="mdi-alert" color="red" size="100"></v-icon></div>
            <div>Erreur de soumission!</div>
          </v-list-item-title>
          <v-list-item-action justify="center" align="center" style="justify-content:center;margin-top:10px;">
            <v-btn @click="showErrorDialog = false" :color="colors.primary">Fermer</v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";
import colors from "../styles/colors";
import axios from "axios";
import api from '../api';

export default {
  data() {
    return {
      isLoading: true,
      loading: false,
      isLastStep: false,
      stepIndex: 0,
      colors,
      currentCandidate: null,
      criterias: [],
      candidatesList: [],
      ratings: {},
      submitDialog: false,
      submissionSuccessDialog: false,
      showErrorDialog: false,
    };
  },
  watch: {
    submitDialog (val) {
      if (!val) return

      setTimeout(() => (this.submitDialog = false), 4000)
    },
  },
  computed: {
    ...mapState(["jury", "candidates", "selectedCandidate", "criteriasByCategory"]),
  },
  methods: {
    ...mapActions(["updateJury", "updateCandidates", "updateCriteriasByCategory", "logout", "updateSelectedCandidate"]),

    parsedCriterias(criterias) {
      console.log(criterias)
      // Parse the JSON string into an object
      //return JSON.parse(criterias);
      return criterias;
    },

    formatExplanation(explanation) {
      if (explanation) {
        return explanation.replace(/\n/g, '<br>');
      }
      return explanation;
    },
    
    // Method to handle candidate selection
    async onCandidateSelected() {
      // Check if a candidate has already been selected
      if (!this.selectedCandidate) {
        if (this.currentCandidate) {
          this.syncCurrentCandidate();
        }
      } else {
        // Confirm with the user before switching candidates
        const userConfirmed = confirm("Vous allez perdre toutes vos données si vous n'avez pas soumis. Voulez-vous continuer ?");
        if (userConfirmed) {
          if (this.currentCandidate) {
            this.syncCurrentCandidate();
          }
        }
      }
    },

    async syncCurrentCandidate() {
      const selected = this.candidatesList.find(candidate => candidate.id === this.currentCandidate);
      if (selected) {
        // Sync the selected candidate with Vuex store
        this.updateSelectedCandidate(selected);

        // Fetch candidate ratings from the API
        this.loading = true; // Show loader
        try {
          const response = await axios.get(api.getCandidateRatingsByJury, {
            params: {
              jury_id: this.jury.id, // Pass the jury ID
              candidate_id: this.currentCandidate, // Pass the current candidate ID
            },
          });
          this.ratings = response.data; // Update ratings with the API response
          console.log(response.data);
        } catch (error) {
          console.error("Error fetching candidate ratings:", error);
        } finally {
          this.loading = false; // Hide loader
        }
      } else {
        console.error("Candidate not found");
      }
    },

    async fetchInitialData() {
      try {
        // Load candidates from API
        const candidatesResponse = await axios.get(api.getCandidates);
        this.candidatesList = candidatesResponse.data;
        //this.updateCandidates(candidatesResponse.data);

        // Load criterias from API
        const criteriasResponse = await axios.get(api.getCriteriasByCategory);
        this.criterias = criteriasResponse.data;

        // Check if jury is selected
        if (!this.jury) {
          this.redirectToSelectJury();
        } else {
          // Load jury data if available
          this.isLoading = false;
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    },

    redirectToSelectJury() {
      this.$router.push({ name: "JurySelection" });
    },

    logoutUser() {
      this.logout();
      this.$nextTick(() => {
        this.$router.push({ name: "JurySelection" });
      });
    },

    nextStep(currentIndex) {
      if (currentIndex < this.criteriasByCategory.length - 1) {
        this.stepIndex = currentIndex + 1;
      } else {
        this.isLastStep = true;
      }
    },

    getJuryImageUrl(filename) { 
      return api.getJuryImageUrl(filename); // Generate the full image URL 
    },

    getCandidateImageUrl(filename) { 
      return api.getCandidateImageUrl(filename); // Generate the full image URL 
    },

    async submitResults() {
      this.submitDialog = true;

      // Prepare data for submission
      const evaluationData = {
        jury_id: this.jury.id,
        candidate_id: this.selectedCandidate.id,
        ratings: this.ratings
      };

      try {
        // Make API call to submit ratings
        const response = await axios.post(api.submitEvaluation, evaluationData);

        if (response.status === 200) {
          // Handle success, show the result dialog with the option to evaluate another candidate or show results
          this.$nextTick(() => {
            this.submitDialog = false;
            this.submissionSuccessDialog = true; // Show the result dialog
          });
        } else {
          throw new Error("Unexpected response status");
        }
      } catch (error) {
        // Handle error, show an error dialog with the message
        this.$nextTick(() => {
          this.submitDialog = false;
          this.showErrorDialog = true; // Show the error dialog
        });
        console.error("Error submitting evaluation:", error);
      }
    },

    // Handle the "Evaluate another candidate" action
    evaluateAnotherCandidate() {
      this.ratings = {}; // Reset candidate selection
      this.submissionSuccessDialog = false;
      this.updateSelectedCandidate(null);  // Reset the selected candidate
      this.stepIndex = 0; // Reset the stepper to step 1
      this.currentCandidate = null; // Reset candidate selection
    },

    // Handle the "Show results" action
    showResults() {
      this.submissionSuccessDialog = false;
      this.$router.push({ name: "ResultsScreen" });
    },
  },
  mounted() {
    this.fetchInitialData();
    this.updateSelectedCandidate(null);
  },
};
</script>
